import { request } from '@umijs/max';
import { registerWsHandler, WsEventType, onSocketReconnected } from '../../websocket';

export interface RescheduleSlot {
  date: string; // Y-m-d
  from_time: string | null; // HH:MM — null when the customer gave a date only
  to_time: string | null;
}

export type ScheduleRequestType = 'reschedule' | 'new_appointment';

export const SERVICE_TYPE_LABELS: Record<string, string> = {
  service_call: 'Service Call',
  pre_season_check: 'Pre-Season Check',
  other: 'Other',
};

/** Warranty Scheduling Form submission from the same AI call (keyed by call_sid). */
export interface RequestJotformSubmission {
  id: number;
  tenant_id: number;
  call_sid: string | null;
  dispatch_id: number | null;
  jotform_form_id: string;
  jotform_submission_id: string;
  unit_age: string | null;
  created_at: string;
}

/** Call-log mirror row for the AI call (lags the request by up to 2 minutes). */
export interface RequestApptCenterCall {
  id: number;
  appt_call_id: number;
  call_sid: string;
  summary: string | null;
  sentiment: string | null;
  topics: string[] | null;
  has_recording: boolean | number;
  recording_id: number | null;
  duration: number | null;
  started_at: string | null;
  customer_name: string | null;
  customer_full_address: string | null;
}

export interface RescheduleRequestRecord {
  id: number;
  tenant_id: number;
  /** reschedule = existing appointment moves; new_appointment = a job is created on accept */
  request_type: ScheduleRequestType;
  call_sid: string | null;
  /** null for a new_appointment request until it is accepted */
  appointment_id: number | null;
  /** null for a new_appointment request until it is accepted */
  dispatch_id: number | null;
  customer_id: number | null;
  customer_number: string;
  service_type: string | null;
  // Caller details collected by the AI (new_appointment only)
  customer_name: string | null;
  customer_email: string | null;
  address_street: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
  notes: string | null;
  source: string;
  status: 'pending' | 'accepted' | 'rejected';
  preferred_slots: RescheduleSlot[];
  accepted_slot: (RescheduleSlot & { technician_id?: number | null }) | null;
  accepted_by: number | null;
  accepted_at: string | null;
  reject_reason: string | null;
  rejected_by: number | null;
  rejected_at: string | null;
  created_at: string;
  updated_at: string;
  dispatch?: {
    id: number;
    customer?: { name?: string | null } | null;
    address?: { full_address?: string | null } | null;
    dispatch_user?: { user_id?: number | null } | null;
  } | null;
  appointment?: {
    id: number;
    user_id?: number | null;
    appointment_date: string | null;
    time_from: string | null;
    time_to: string | null;
  } | null;
  /** Matched existing customer (by id or phone) for a new_appointment request */
  customer?: { id: number; name?: string | null; email?: string | null } | null;
  jotform_submissions?: RequestJotformSubmission[];
  appt_center_call?: RequestApptCenterCall | null;
  accepted_by_user?: { id: number; user_name?: string | null } | null;
  rejected_by_user?: { id: number; user_name?: string | null } | null;
}

export interface RescheduleRequestListResult {
  data: RescheduleRequestRecord[];
  success: boolean;
  total: number;
}

export async function listRescheduleRequests(params: {
  status?: string;
  request_type?: ScheduleRequestType;
  search?: string;
  current?: number;
  pageSize?: number;
}): Promise<RescheduleRequestListResult> {
  return request<API.ResultObject<{ rows: RescheduleRequestRecord[]; pagination: API.Pagination }>>(
    '/api/tenant/reschedule-requests',
    {
      method: 'GET',
      params: {
        status: params.status,
        request_type: params.request_type,
        search: params.search,
        page: params.current,
        perPage: params.pageSize,
      },
    },
  ).then((res) => ({
    data: res.data.rows ?? [],
    success: res.success ?? true,
    total: res.data.pagination?.total_records ?? 0,
  }));
}

/** Accept a reschedule request: the CSR always sets the final window. */
export async function acceptRescheduleRequest(
  id: number,
  data: { appointment_date: string; time_from: string; time_to: string },
) {
  return request<API.ResultObject<any>>(`/api/tenant/reschedule-requests/${id}/accept`, {
    method: 'POST',
    data,
  });
}

export interface AcceptNewAppointmentResult {
  request_id: number;
  status: string;
  dispatch_id: number;
  appointment_id: number | null;
  customer_id: number | null;
  customer_created: boolean;
  submissions_linked: number;
  auto_schedule:
    | { status: 'scheduled'; appointment_id: number; technician: string | null; appointment_date: string; time_from: string; time_to: string }
    | { status: 'failed'; reason: string }
    | null;
}

/**
 * Accept a new-appointment request: creates the customer (when unknown), the
 * job and — with technician + window — the appointment. Date only hands the
 * date to the Automation Builder (same as creating a direct job without a tech).
 */
export async function acceptNewAppointmentRequest(
  id: number,
  data: { appointment_date: string; technician_id?: number | null; time_from?: string | null; time_to?: string | null },
) {
  return request<API.ResultObject<AcceptNewAppointmentResult>>(`/api/tenant/reschedule-requests/${id}/accept`, {
    method: 'POST',
    data,
  });
}

export async function rejectRescheduleRequest(id: number, data: { reason?: string }) {
  return request<API.ResultObject<any>>(`/api/tenant/reschedule-requests/${id}/reject`, {
    method: 'POST',
    data,
  });
}

export type PendingRescheduleSnapshot = {
  count: number;
  max_id: number;
  count_reschedule?: number;
  count_new_appointment?: number;
};

/** Lightweight poll for the global banner. */
export async function getPendingRescheduleRequestCount() {
  return request<API.ResultObject<PendingRescheduleSnapshot>>(
    '/api/tenant/reschedule-requests/pending-count',
    { method: 'GET' },
  ).then((res) => res.data);
}

// ─── Reschedule-request event bus (mirrors the call-queue WS pattern) ─────────
const RESCHEDULE_REQUEST_UPDATED_EVENT = 'rescheduleRequestUpdated';

export type RescheduleRequestUpdatedPayload = PendingRescheduleSnapshot;

/**
 * `data` carries the pending snapshot straight off the WS push (see
 * AppointmentRescheduleRequest::broadcastPending() on the backend), so a
 * listener can update state directly instead of turning around and
 * re-fetching over REST. Undefined when the push is signal-only (a socket
 * reconnect, or an older server) — callers should fall back to a REST fetch.
 */
export function emitRescheduleRequestUpdated(data?: RescheduleRequestUpdatedPayload) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<RescheduleRequestUpdatedPayload | undefined>(RESCHEDULE_REQUEST_UPDATED_EVENT, { detail: data }));
}

/** Subscribe to pending-count pushes; returns an unsubscribe function. */
export function onRescheduleRequestUpdated(handler: (data?: RescheduleRequestUpdatedPayload) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<RescheduleRequestUpdatedPayload | undefined>).detail);
  window.addEventListener(RESCHEDULE_REQUEST_UPDATED_EVENT, listener);
  return () => window.removeEventListener(RESCHEDULE_REQUEST_UPDATED_EVENT, listener);
}

// Coalesce a burst of pushes into one apply — safe because each push already carries
// the full pending-count snapshot, so only the latest one need ever reach the listener.
registerWsHandler(WsEventType.RESCHEDULE_REQUEST_UPDATED, emitRescheduleRequestUpdated, { coalesceMs: 300 });

// A dropped push during a disconnect never arrives on its own — resync the
// instant the socket comes back instead of waiting out the 60s poll fallback.
onSocketReconnected(() => emitRescheduleRequestUpdated());
